

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Chip,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Card,
  CardMedia, Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid
} from '@mui/material';
import {
  Close,
  ExpandMore,
  AddPhotoAlternate,
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
  Edit,
  Save,
  Warning,
  CheckCircle,
  Error
} from '@mui/icons-material';
import axiosInstance from '../lib/axios';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '& .MuiTableCell-root': {
    padding: '8px 4px',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '8px 4px',
  },
}));



const PhotoUploadButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '3px 6px',
  fontSize: '0.75rem'
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
  border: '1px solid #e0e0e0',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: '16px 0'
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: '48px !important',
  padding: '0 16px',
  '& .MuiAccordionSummary-content': {
    margin: '12px 0 !important'
  }
}));

const SummaryBox = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2)
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 'bold',
  ...(status === 'damaged' && {
    backgroundColor: '#ffebee',
    color: '#c62828',
    border: '1px solid #ffcdd2'
  }),
  ...(status === 'lost' && {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    border: '1px solid #ffe0b2'
  }),
  ...(status === 'ok' && {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
    border: '1px solid #c8e6c9'
  })
}));

const CheckinArtical = ({ open, onClose, bookId, roomTypeId, roomBedTypeId ,onDataUpdate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState({});
  const [selectedArticles, setSelectedArticles] = useState({});
  const [expandedCategory, setExpandedCategory] = useState({});
  const [uploadedPhotos, setUploadedPhotos] = useState({});
  const [imageNames, setImageNames] = useState({});
  const [originalPhotos, setOriginalPhotos] = useState({});
  const [remarks, setRemarks] = useState({});
  const [damageCharges, setDamageCharges] = useState({});
  const [lostCharges, setLostCharges] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [damageStatus, setDamageStatus] = useState({});

  useEffect(() => {
    if (open && bookId && roomTypeId && roomBedTypeId) {
      fetchCheckoutItems();
      setSelectedArticles({});
      setUploadedPhotos({});
      setImageNames({});
      setOriginalPhotos({});
      setRemarks({});
      setDamageCharges({});
      setLostCharges({});
      setEditingRows({});
      setDamageStatus({});
    }
  }, [open, bookId, roomTypeId, roomBedTypeId]);

  const fetchCheckoutItems = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`TrnChkArticle/RetriveTrnChkArt`, {
        BookId: bookId,
        RoomTypeId: roomTypeId,
        RoomBedTypeId: roomBedTypeId,
        Flag: "TrnChkOut"
      });

      if (response.data.Status === 0 && response.data.Data && response.data.Data.TrnChkArtList) {
        const mainItems = response.data.Data.TrnChkArtList;
        setItems(mainItems);
        const initialExpanded = {};
        const initialSelected = {};
        const initialRemarks = {};
        const initialDamageCharges = {};
        const initialLostCharges = {};
        const initialUploadedPhotos = {};
        const initialImageNames = {};
        const initialOriginalPhotos = {};
        const initialStatus = {};
        let firstCategoryExpanded = false;

        mainItems.forEach((mainItem, mainIndex) => {
          if (mainItem.TrnChkArtList && mainItem.TrnChkArtList.length > 0) {
            mainItem.TrnChkArtList.forEach((article, artIndex) => {
              const key = `${mainIndex}-${article.ChkArtCatName}`;
              const articleKey = `${mainIndex}-${article.ChkArticleId}`;
              initialExpanded[key] = true;

              initialSelected[articleKey] = true;
              initialRemarks[articleKey] = article.ArtRemk2 || '';
              initialDamageCharges[articleKey] = article.DmzChrg || 0;
              initialLostCharges[articleKey] = article.StdRate || 0;
              initialStatus[articleKey] = article.Status || 'O'

              if (article.ArtImg2) {
                const fixedUrl = article.ArtImg2.replace('http:/', 'http://');
                initialUploadedPhotos[articleKey] = fixedUrl;
                initialImageNames[articleKey] = article.ImgName1 || `Image-${article.ChkArtDtlId}`;
                initialOriginalPhotos[articleKey] = fixedUrl;
                console.log(`Setting image for ${articleKey}: ${fixedUrl}`);
              }
            });
          }
        });
        setDamageStatus(initialStatus)
        setExpandedCategory(initialExpanded);
        setSelectedArticles(initialSelected);
        setRemarks(initialRemarks);
        setDamageCharges(initialDamageCharges);
        setLostCharges(initialLostCharges);
        setUploadedPhotos(initialUploadedPhotos);
        setImageNames(initialImageNames);
        setOriginalPhotos(initialOriginalPhotos);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching checkout items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDamageStatusChange = (mainIndex, articleId, value) => {
    const articleKey = `${mainIndex}-${articleId}`;
    console.log(articleKey, 'articleKey')
    setDamageStatus(prev => ({
      ...prev,
      [articleKey]: value
    }));
  };

  const handleArticleSelection = (mainIndex, articleId, isSelected) => {
    setSelectedArticles(prev => ({
      ...prev,
      [`${mainIndex}-${articleId}`]: isSelected
    }));
  };

  const handleCategoryToggle = (key) => {
    setExpandedCategory(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePhotoUpload = (event, mainIndex, articleId) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const articleKey = `${mainIndex}-${articleId}`;
        console.log('Uploaded image base64:', e.target.result);
        if (e.target.result.startsWith('data:image/')) {
          setUploadedPhotos(prev => ({
            ...prev,
            [articleKey]: e.target.result
          }));
          setImageNames(prev => ({
            ...prev,
            [articleKey]: file.name
          }));
        } else {
          console.error('Invalid image file uploaded');
          toast.error('Please upload a valid image file');
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handlePhotoRemove = (mainIndex, articleId) => {
    const articleKey = `${mainIndex}-${articleId}`;
    setUploadedPhotos(prev => {
      const newPhotos = { ...prev };
      delete newPhotos[articleKey];
      return newPhotos;
    });
    setImageNames(prev => {
      const newNames = { ...prev };
      delete newNames[articleKey];
      return newNames;
    });
  };

  const handleRemarkChange = (mainIndex, articleId, value) => {
    setRemarks(prev => ({
      ...prev,
      [`${mainIndex}-${articleId}`]: value
    }));
  };

  const handleDamageChargeChange = (mainIndex, articleId, value) => {
    const numValue = value === '' ? 0 : (parseFloat(value) || 0);
    setDamageCharges(prev => ({
      ...prev,
      [`${mainIndex}-${articleId}`]: numValue
    }));
  };

  const handleLostChargeChange = (mainIndex, articleId, value) => {
    const numValue = value === '' ? 0 : (parseFloat(value) || 0);
    setLostCharges(prev => ({
      ...prev,
      [`${mainIndex}-${articleId}`]: numValue
    }));
  };

  const handleEditToggle = (mainIndex, articleId) => {
    const articleKey = `${mainIndex}-${articleId}`;
    setEditingRows(prev => ({
      ...prev,
      [articleKey]: !prev[articleKey]
    }));
  };

  const handleSave = async (mainIndex, articleId, chkArtDtlId) => {
  const articleKey = `${mainIndex}-${articleId}`;
  setSubmitting(prev => ({ ...prev, [articleKey]: true }));

  try {
    const hasNewImage = uploadedPhotos[articleKey] && uploadedPhotos[articleKey] !== originalPhotos[articleKey];
    const isImageRemoved = originalPhotos[articleKey] && !uploadedPhotos[articleKey];
    const artImg2 = hasNewImage ? uploadedPhotos[articleKey] : (isImageRemoved ? '' : '');
    const imgName1 = hasNewImage ? imageNames[articleKey] || '' : (isImageRemoved ? '' : '');
    const stausvalue = damageStatus[articleKey] === '1' ? 'O' : damageStatus[articleKey]
    
    // FIX: Damage charge ko preserve karo, kisi bhi status mein zero mat karo
    let chargeAmount = damageCharges[articleKey] || 0;
    
    // Sirf Lost status ke liye lost charge use karo
    if (damageStatus[articleKey] === 'L') {
      chargeAmount = lostCharges[articleKey] || 0;
    }
    // 'O' (Ok) aur 'D' (Damaged) status ke liye existing damage charge hi preserve rahega

    const response = await axiosInstance.post('TrnChkArticle/TrnChkArtOut', {
      ChkArtDtlId: chkArtDtlId,
      ChkArtId: items[mainIndex]?.ChkArtId || 0,
      DmzChrg: chargeAmount, // Yeh ab kisi bhi status mein zero nahi hoga
      ArtImg2: artImg2,
      ImgName1: imgName1,
      ArtRemk2: remarks[articleKey] || '',
      Status: stausvalue
    }, {
      headers: {
        'Authorization': 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJWYWxpZElzc3VlciI6IlByYXN5c3QiLCJWYWxpZEF1ZGllbmNlIjoiUHJhc3lzdCJ9.gMMGQ74cWLoymeDv0D4c3UQKTEJSCzdkXoxlW4qF5QU'
      }
    });

    if (response.data.Status === 0) {
      toast.success('Article checked out successfully', {
        autoClose: 1000
      });
      setEditingRows(prev => ({
        ...prev,
        [articleKey]: false
      }));
      await fetchCheckoutItems();
    } else {
      console.error('Error checking out article:', response.data.Message);
      toast.error(response.data.Message || 'Failed to check out article');
    }
  } catch (error) {
    console.error('Error checking out article:', error);
    toast.error('Failed to check out article');
  } finally {
    setSubmitting(prev => ({ ...prev, [articleKey]: false }));
  }
};

  const groupArticlesByCategory = (articles) => {
    const categories = {};
    articles.forEach(article => {
      if (!categories[article.ChkArtCatName]) {
        categories[article.ChkArtCatName] = [];
      }
      categories[article.ChkArtCatName].push(article);
    });
    return categories;
  };

  // Function to calculate damage summary for a category
  const getCategorySummary = (articles, mainIndex) => {
    let okCount = 0;
    let damagedCount = 0;
    let lostCount = 0;
    let totalDamageCharges = 0;
    let totalLostCharges = 0;

    articles.forEach(article => {
      const articleKey = `${mainIndex}-${article.ChkArticleId}`;
      const status = damageStatus[articleKey] || 'O';
      
      if (status === 'O') {
        okCount++;
      } else if (status === 'D') {
        damagedCount++;
        totalDamageCharges += damageCharges[articleKey] || 0;
      } else if (status === 'L') {
        lostCount++;
        totalLostCharges += lostCharges[articleKey] || 0;
      }
    });

    return {
      total: articles.length,
      ok: okCount,
      damaged: damagedCount,
      lost: lostCount,
      totalDamageCharges: totalDamageCharges,
      totalLostCharges: totalLostCharges,
      totalCharges: totalDamageCharges + totalLostCharges
    };
  };

  const hasArticles = items.some(mainItem => mainItem.TrnChkArtList && mainItem.TrnChkArtList.length > 0);
  const handleClose = () => {
    if (onDataUpdate) {
      onDataUpdate({
        items,
        damageStatus,
        damageCharges,
        lostCharges,
        remarks,
        uploadedPhotos
      });
    }
    onClose();
  };
  
  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{
        m: 0,
        display: 'flex',
        padding: '0px 7px',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
        color: 'white'
      }}>
        <Typography variant="h6" fontWeight="bold">Check-in Articles</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'white',
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : items.length > 0 ? (
          <Box>

            {hasArticles && (
              <TableContainer sx={{ mb: 1 }}>
                <Table size="small">
                  <StyledTableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '50px' }}>Select</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '120px', textAlign: 'center' }}>Check-in Photo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '230px', textAlign: 'center' }}>Damage Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Std Rate</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Lost Charge</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Damage Charge</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '180px' }}>Check-out Photo</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '150px' }}>Remark</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '50px' }}>Action</TableCell>
                    </TableRow>
                  </StyledTableHead>
                </Table>
              </TableContainer>
            )}

            {items.map((mainItem, mainIndex) => (
              <Box key={mainIndex} sx={{ mb: 1, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                {mainItem.TrnChkArtList && mainItem.TrnChkArtList.length > 0 ? (
                  <Box>
                    {Object.entries(groupArticlesByCategory(mainItem.TrnChkArtList)).map(([category, articles], catIndex) => {
                      const categoryKey = `${mainIndex}-${category}`;
                      const summary = getCategorySummary(articles, mainIndex);
                      
                      return (
                        <StyledAccordion
                          key={categoryKey}
                          expanded={expandedCategory[categoryKey] || false}
                          onChange={() => handleCategoryToggle(categoryKey)}
                        >
                          <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography fontWeight="bold" variant="subtitle2">{category}</Typography>
                          </StyledAccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>
                            <TableContainer>
                              <Table size="small">
                                <TableBody>
                                  {articles.map((article, artIndex) => {
                                    const articleKey = `${mainIndex}-${article.ChkArticleId}`;
                                    const isEditing = editingRows[articleKey];
                                    const displayImage = uploadedPhotos[articleKey] || article.ArtImg2;
                                    const checkinImage = article.ArtImg1 ? article.ArtImg1.replace('http:/', 'http://') : null;

                                    return (
                                      <StyledTableRow key={articleKey}>
                                        <TableCell sx={{ width: '50px' }}>
                                          <Checkbox
                                            icon={<CheckBoxOutlineBlank />}
                                            checkedIcon={<CheckBox />}
                                            checked={!!selectedArticles[articleKey]}
                                            onChange={(e) => handleArticleSelection(mainIndex, article.ChkArticleId, e.target.checked)}
                                          />
                                        </TableCell>
                                        <TableCell>{article.ChkArticleName || '-'}</TableCell>

                                        <TableCell sx={{ width: '120px' }}>
                                          {checkinImage ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                              <Card sx={{ width: 60, height: 60 }}>
                                                <CardMedia
                                                  component="img"
                                                  height="60"
                                                  image={checkinImage}
                                                  alt="Check-in Photo"
                                                  sx={{
                                                    objectFit: 'cover',
                                                    width: '100%',
                                                    height: '100%'
                                                  }}
                                                  onError={(e) => {
                                                    console.error(`Failed to load check-in image: ${checkinImage}`);
                                                    e.target.src = 'https://via.placeholder.com/60?text=No+Image';
                                                  }}
                                                />
                                              </Card>
                                            </Box>
                                          ) : (
                                            <Typography
                                              variant="caption"
                                              align="center"
                                              sx={{
                                                display: 'block',
                                                color: 'text.secondary',
                                                fontStyle: 'italic'
                                              }}
                                            >
                                              No Photo
                                            </Typography>
                                          )}
                                        </TableCell>

                                        <TableCell sx={{ width: '250px' }}>
                                          <FormControl component="fieldset" size="small">
                                            <RadioGroup
                                              row
                                              value={damageStatus[articleKey] === '1' ? 'O' : damageStatus[articleKey]}
                                              sx={{
                                                fontSize: '15px',
                                                '& .MuiFormControlLabel-label': {
                                                  fontSize: '15px',
                                                }
                                              }}
                                              onChange={(e) => handleDamageStatusChange(mainIndex, article.ChkArticleId, e.target.value)}
                                            >
                                              <FormControlLabel
                                                value="O"
                                                control={<Radio size="small" sx={{ padding: '3px' }} />}
                                                label="Ok"
                                                sx={{ marginRight: '10px' }}
                                              />
                                              <FormControlLabel
                                                value="D"
                                                control={<Radio size="small" sx={{ padding: '3px' }} />}
                                                label="Damaged"
                                                sx={{ marginRight: '10px' }}
                                              />
                                              <FormControlLabel
                                                value="L"
                                                control={<Radio size="small" sx={{ padding: '3px' }} />}
                                                label="Lost"
                                                sx={{ marginRight: '0px' }}
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                        </TableCell>
                                        <TableCell align="center">₹{article.StdRate?.toFixed(2) || '0.00'}</TableCell>

                                        {/* Lost Charge Column */}
                                        <TableCell>
                                          <TextField
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={lostCharges[articleKey] !== undefined ? lostCharges[articleKey] : (article.StdRate || 0)}
                                            onChange={(e) => handleLostChargeChange(mainIndex, article.ChkArticleId, e.target.value)}
                                            sx={{
                                              maxWidth: '100px',
                                              p: 0,
                                              '& .MuiInputBase-root': {
                                                height: '30px',
                                                paddingLeft: '4px',
                                              },
                                            }}
                                            InputProps={{
                                              startAdornment: <span style={{ marginRight: 4 }}>₹</span>,
                                            }}
                                            disabled={true}
                                          />
                                        </TableCell>

                                        {/* Damage Charge Column */}
                                        <TableCell>
                                          <TextField
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={damageCharges[articleKey] !== undefined ? damageCharges[articleKey] : (article.DmzChrg || 0)}
                                            onChange={(e) => handleDamageChargeChange(mainIndex, article.ChkArticleId, e.target.value)}
                                            sx={{
                                              maxWidth: '100px',
                                              p: 0,
                                              '& .MuiInputBase-root': {
                                                height: '30px',
                                                paddingLeft: '4px',
                                              },
                                            }}
                                            InputProps={{
                                              startAdornment: <span style={{ marginRight: 4 }}>₹</span>,
                                            }}
                                            disabled={true}
                                          />
                                        </TableCell>

                                        <TableCell sx={{ width: '180px' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <input
                                              accept="image/*"
                                              style={{ display: 'none' }}
                                              id={`photo-upload-${articleKey}`}
                                              type="file"
                                              key={`photo-input-${articleKey}-${uploadedPhotos[articleKey] ? 'uploaded' : 'empty'}`}
                                              onChange={(e) => handlePhotoUpload(e, mainIndex, article.ChkArticleId)}
                                            />
                                            <label htmlFor={`photo-upload-${articleKey}`}>
                                              <PhotoUploadButton
                                                variant="outlined"
                                                component="span"
                                                size="small"
                                                startIcon={<AddPhotoAlternate />}
                                              >
                                                Upload
                                              </PhotoUploadButton>
                                            </label>
                                            {displayImage && (
                                              <Box sx={{ position: 'relative' }}>
                                                <Card sx={{ width: 60, height: 60 }}>
                                                  <CardMedia
                                                    component="img"
                                                    height="60"
                                                    image={displayImage}
                                                    alt="Check-out Photo"
                                                    sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                                    onError={(e) => {
                                                      console.error(`Failed to load check-out image: ${displayImage}`);
                                                      e.target.src = 'https://via.placeholder.com/60?text=No+Image';
                                                    }}
                                                  />
                                                </Card>
                                                <IconButton
                                                  size="small"
                                                  sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    backgroundColor: 'white',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    '&:hover': {
                                                      backgroundColor: 'grey.100'
                                                    }
                                                  }}
                                                  onClick={() => handlePhotoRemove(mainIndex, article.ChkArticleId)}
                                                >
                                                  <Delete fontSize="small" sx={{ color: 'red' }} />
                                                </IconButton>
                                              </Box>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '150px' }}>
                                          <TextField
                                            size="small"
                                            fullWidth
                                            value={remarks[articleKey] || ''}
                                            onChange={(e) => handleRemarkChange(mainIndex, article.ChkArticleId, e.target.value)}
                                            placeholder="Add remark"
                                            sx={{
                                              maxWidth: '250px',
                                              p: 0,
                                              '& .MuiInputBase-root': {
                                                height: '30px',
                                                paddingLeft: '0.5px',
                                              },
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ width: '50px' }}>
                                          <Checkbox
                                            icon={<CheckBoxOutlineBlank />}
                                            checkedIcon={<CheckBox sx={{ color: 'green' }} />}
                                            checked={damageStatus[articleKey] === 'O' || damageStatus[articleKey] === 'L' || damageStatus[articleKey] === 'D'}
                                            onChange={() => handleSave(mainIndex, article.ChkArticleId, article.ChkArtDtlId)}
                                            disabled={submitting[articleKey]}
                                            sx={{
                                              color: 'grey',
                                              '&.Mui-checked': {
                                                color: 'green',
                                              },
                                            }}
                                          />
                                        </TableCell>
                                      </StyledTableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            {/* Damage Summary at the bottom of each accordion */}
                            <SummaryBox>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                  Category Summary:
                                </Typography>
                                <StatusChip 
                                  icon={<CheckCircle fontSize="small" />}
                                  label={`${summary.ok} OK`}
                                  status="ok"
                                  size="small"
                                />
                                <StatusChip 
                                  icon={<Warning fontSize="small" />}
                                  label={`${summary.damaged} Damaged`}
                                  status="damaged"
                                  size="small"
                                />
                                <StatusChip 
                                  icon={<Error fontSize="small" />}
                                  label={`${summary.lost} Lost`}
                                  status="lost"
                                  size="small"
                                />
                                <Typography variant="body2" color="text.secondary">
                                  Total Items: {summary.total}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {summary.damaged > 0 && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" color="error.main">
                                      Damage Charges:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color="error.main">
                                      ₹{summary.totalDamageCharges.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                                {summary.lost > 0 && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" color="warning.main">
                                      Lost Charges:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold" color="warning.main">
                                      ₹{summary.totalLostCharges.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                    Total Charges:
                                  </Typography>
                                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                                    ₹{summary.totalCharges.toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                            </SummaryBox>
                          </AccordionDetails>
                        </StyledAccordion>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" align="center" sx={{ py: 3 }}>
                    No article details found
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" align="center" sx={{ py: 3 }}>
            No check-in articles found
          </Typography>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default CheckinArtical;